import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="reset-invalid">
        <div className="text-center">
          <p className="text-lg text-[#666]">Link non valido o incompleto.</p>
          <Link to="/forgot-password" className="mt-4 inline-block text-[#0055FF] font-medium">
            Richiedi un nuovo link
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="font-heading text-2xl font-semibold tracking-tight">
            auto<span className="text-[#0055FF]">.</span>advisor
          </Link>
          <h1 className="mt-6 font-heading text-3xl font-semibold tracking-tight">
            {done ? "Password aggiornata" : "Nuova password"}
          </h1>
          <p className="mt-2 text-sm text-[#666]">
            {done ? "Ti stiamo reindirizzando al login…" : "Scegli una nuova password sicura per il tuo account."}
          </p>
        </div>

        {done ? (
          <div className="rounded-2xl border border-[#EAEAEA] bg-white p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-[#0055FF] mx-auto" />
            <p className="mt-4 font-medium" data-testid="reset-success">Tutto pronto!</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
              <Input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Nuova password (min 6)"
                minLength={6}
                className="h-12 pl-11 rounded-2xl border-[#EAEAEA] bg-white"
                data-testid="reset-password-input"
              />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
              <Input
                required
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Ripeti password"
                minLength={6}
                className="h-12 pl-11 rounded-2xl border-[#EAEAEA] bg-white"
                data-testid="reset-confirm-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full h-12 rounded-full bg-[#111] text-white hover:bg-[#333] transition disabled:opacity-60"
              data-testid="reset-submit"
            >
              {busy ? "Aggiornamento…" : "Aggiorna password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
